import { API_BASE_URL } from 'lib/config/apiEndpoints';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import {
  BvUpsellCard,
  type BvUsPlanKey,
  getAllUsBvVariantGidsForPlanKey,
  getBloodVisionUpsellCards,
} from 'content/products/bloodVisionUpsell';

const BV_US_PLAN_KEYS: BvUsPlanKey[] = [
  'fasting_insulin',
  'hormone_health_female',
  'hormone_health_male',
];

type BvCartLinesInput = {
  lines?: {
    edges?: Array<{
      node?: { merchandise?: { id?: string } };
    }>;
  } | null;
} | null;

/**
 * Matches cart lines against a single Shopify variant ID (region-specific for US).
 * Pass the same variant used for ATC (`getBvShopifyVariantIdForAtc`) when ZIP pricing is resolved.
 */
export function isBvCardInCart(
  card: BvUpsellCard,
  cart: BvCartLinesInput | null | undefined,
  store: ShopifyStore | null,
  variantIdForMatch?: string | null,
): boolean {
  const edges = cart?.lines?.edges;
  if (!store || !edges?.length) return false;

  const candidateId = variantIdForMatch ?? card.shopifyVariantId;
  if (!candidateId) return false;

  const normalizedCandidate = normalizeShopifyVariantId(candidateId);

  for (const edge of edges) {
    if (
      normalizedCandidate ===
      normalizeShopifyVariantId(edge?.node?.merchandise?.id ?? '')
    ) {
      return true;
    }
  }
  return false;
}

export function getAllBvUpsellVariantIdsForStore(
  store: ShopifyStore,
): string[] {
  if (store === ShopifyStore.US) {
    return [
      ...new Set(
        BV_US_PLAN_KEYS.flatMap((key) => getAllUsBvVariantGidsForPlanKey(key)),
      ),
    ];
  }
  return getBloodVisionUpsellCards(store).map((c) => String(c.shopifyVariantId));
}

export interface BvResolvedUsPlanDisplay {
  title: string;
  description: string;
  imageSrc: string;
  markers?: number;
}

export interface BvPincodeValidationResult {
  isServiceable: boolean;
  resolvedPrice?: number;
  resolvedUsPlanDisplay?: BvResolvedUsPlanDisplay;
  resolvedShopifyVariantId?: string;
  reasonCode?:
    | 'missing_pincode'
    | 'invalid_pincode_format'
    | 'not_serviceable'
    | 'request_failed'
    | 'unknown';
  message: string;
}

export interface BvStoreInputConfig {
  checkSectionTitle: string;
  inputLabel: string;
  inputPlaceholder: string;
  inputMaxLength: number;
  pincodeRegex: RegExp;
  invalidFormatMessage: string;
  missingValueMessage: string;
  defaultAvailabilityHint: string;
}

export const getBvStoreInputConfig = (
  store: ShopifyStore | null,
): BvStoreInputConfig => {
  if (store === ShopifyStore.US) {
    return {
      checkSectionTitle: 'Check ZIP code',
      inputLabel: 'ZIP code',
      inputPlaceholder: '12345',
      inputMaxLength: 5,
      pincodeRegex: /^\d{5}$/,
      invalidFormatMessage: 'Please enter a 5 digit valid ZIP code',
      missingValueMessage: 'Enter ZIP code to continue.',
      defaultAvailabilityHint:
        'Enter your ZIP code to check the final price for your region',
    };
  }
  if (store === ShopifyStore.AE) {
    return {
      checkSectionTitle: 'Check serviceability',
      inputLabel: 'City',
      inputPlaceholder: 'Type your city',
      inputMaxLength: 64,
      pincodeRegex: /^[\p{L}\p{N}][\p{L}\p{N}\s'.-]{1,63}$/u,
      invalidFormatMessage: 'Please enter at least 2 characters to find your city.',
      missingValueMessage: 'Enter city to continue.',
      defaultAvailabilityHint: 'Enter your city to check availability',
    };
  }
  if (store === ShopifyStore.IN) {
    return {
      checkSectionTitle: 'Check PIN code',
      inputLabel: 'PIN code',
      inputPlaceholder: '560001',
      inputMaxLength: 6,
      pincodeRegex: /^[1-9]\d{5}$/,
      invalidFormatMessage: 'Please enter a 6 digit valid pincode',
      missingValueMessage: 'Enter pincode to continue.',
      defaultAvailabilityHint:
        'Enter your PIN code to check service availability in your region',
    };
  }
  return {
    checkSectionTitle: 'Check PIN code',
    inputLabel: 'PIN code',
    inputPlaceholder: '560001',
    inputMaxLength: 6,
    pincodeRegex: /^[1-9]\d{5}$/,
    invalidFormatMessage: 'Please enter a 6 digit valid pincode',
    missingValueMessage: 'Enter pincode to continue.',
    defaultAvailabilityHint: 'Enter pincode to check plan availability.',
  };
};

export const getBvLearnMoreLinkLabel = (
  store: ShopifyStore | null,
): string =>
  store === ShopifyStore.US
    ? 'Check price in your region'
    : 'Check serviceability';

export const sanitizeBvPrice = (value?: number): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? value
    : undefined;

export const resolveBvStatusKind = (
  result: Pick<BvPincodeValidationResult, 'isServiceable' | 'reasonCode'>,
): 'error' | 'warning' | 'success' => {
  if (result.isServiceable) return 'success';
  if (result.reasonCode === 'not_serviceable') return 'warning';
  return 'error';
};

export interface UsBvShopifyPlanRow {
  title?: string;
  description?: string;
  markers?: number;
  price?: number;
  sku?: string;
  variant_id?: string;
  image_url?: string;
}

export interface UsBvShopifyPlansResponse {
  status?: string;
  data?: {
    region?: string;
    region_label?: string;
    plans?: UsBvShopifyPlanRow[];
  };
}

interface UsBvServiceabilityResponse {
  status?: string;
  data?: {
    is_serviceable?: boolean;
  };
}

interface InBvPincodeInfo {
  city?: string;
  state?: string;
}

interface InBvPackageListResponse {
  status?: string;
  data?: {
    pincode_serviceable?: boolean;
    pincode_info?: InBvPincodeInfo;
  };
}

export interface AeSupportedCity {
  id: number;
  name: string;
  name_ar?: string;
}

interface AeSupportedCitiesResponse {
  status?: string;
  error?: string | null;
  data?: {
    cities?: AeSupportedCity[];
  } | null;
}

export const isValidUsZip = (zipCode: string) => /^\d{5}$/.test(zipCode.trim());
const isValidInPincode = (pincode: string) => /^[1-9]\d{5}$/.test(pincode.trim());

export const normalizeShopifyVariantId = (variantId: string): string => {
  const trimmed = variantId.trim();
  const gidMatch = trimmed.match(/ProductVariant\/(\d+)\s*$/);
  if (gidMatch) return gidMatch[1];
  if (/^\d+$/.test(trimmed)) return trimmed;
  return trimmed;
};

const parseUsBvPlanPrice = (row: UsBvShopifyPlanRow): number | undefined => {
  const raw = row.price as unknown;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string') {
    const n = Number(raw);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
};

export const toShopifyVariantGid = (variantId: string): string => {
  const trimmed = variantId.trim();
  if (trimmed.startsWith('gid://shopify/ProductVariant/')) return trimmed;
  const numeric = normalizeShopifyVariantId(trimmed);
  if (/^\d+$/.test(numeric)) {
    return `gid://shopify/ProductVariant/${numeric}`;
  }
  return trimmed;
};

export const normalizeAeCityForMatch = (value: string): string =>
  value
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^\p{L}\p{N}]+/gu, '');

export const getAeCityCandidateTokens = (value: string): string[] => {
  const compact = normalizeAeCityForMatch(value);
  const rawTokens = value
    .toLowerCase()
    .normalize('NFKD')
    .split(/[^\p{L}\p{N}]+/gu)
    .filter(Boolean);
  return Array.from(new Set([compact, ...rawTokens])).filter(Boolean);
};

const getAeCityNormalizedNames = (
  city: Pick<AeSupportedCity, 'name' | 'name_ar'>,
): string[] =>
  [city.name, city.name_ar]
    .filter((value): value is string => typeof value === 'string' && !!value.trim())
    .map((value) => normalizeAeCityForMatch(value));

/** Exact match only — used for serviceability / ATC (not autocomplete). */
export const cityEqualsAeSupportedCity = (
  cityInput: string,
  city: Pick<AeSupportedCity, 'name' | 'name_ar'>,
): boolean => {
  const normalizedInput = normalizeAeCityForMatch(cityInput.trim());
  if (!normalizedInput) return false;
  const cityCandidates = getAeCityNormalizedNames(city);
  return cityCandidates.some((candidate) => candidate === normalizedInput);
};

/** Fuzzy / prefix match — used for city suggestion list only. */
export const cityMatchesAeSupportedCity = (
  cityInput: string,
  city: Pick<AeSupportedCity, 'name' | 'name_ar'>,
): boolean => {
  const queryTokens = getAeCityCandidateTokens(cityInput);
  if (queryTokens.length === 0) return false;
  const cityCandidates = getAeCityNormalizedNames(city);
  if (cityCandidates.length === 0) return false;

  const hasExact = queryTokens.some((token) => cityCandidates.includes(token));
  if (hasExact) return true;

  return queryTokens.some((token) =>
    cityCandidates.some(
      (candidate) => candidate.includes(token) || token.includes(candidate),
    ),
  );
};

const AE_CITY_POPOVER_MAX_HEIGHT = 220;
const AE_CITY_POPOVER_ROW_HEIGHT = 42;
const AE_CITY_POPOVER_PADDING = 12;

export type AeCityPopoverPlacement = 'above' | 'below';

export type AeCityPopoverFixedStyle = {
  position: 'fixed';
  top: number;
  left: number;
  width: number;
  zIndex: number;
  transform?: string;
};

export const getAeCitySuggestionPopoverLayout = (
  inputRect: Pick<DOMRect, 'top' | 'bottom' | 'left' | 'width'>,
  suggestionCount: number,
): { placement: AeCityPopoverPlacement; style: AeCityPopoverFixedStyle } => {
  const estimatedHeight = Math.min(
    AE_CITY_POPOVER_MAX_HEIGHT,
    Math.max(suggestionCount, 1) * AE_CITY_POPOVER_ROW_HEIGHT +
      AE_CITY_POPOVER_PADDING,
  );
  const spaceBelow =
    typeof window !== 'undefined'
      ? window.innerHeight - inputRect.bottom
      : estimatedHeight;
  const spaceAbove = inputRect.top;
  const placeAbove = spaceBelow < estimatedHeight && spaceAbove > spaceBelow;

  if (placeAbove) {
    return {
      placement: 'above',
      style: {
        position: 'fixed',
        top: inputRect.top,
        left: inputRect.left,
        width: inputRect.width,
        transform: 'translateY(-100%)',
        zIndex: 5000,
      },
    };
  }

  return {
    placement: 'below',
    style: {
      position: 'fixed',
      top: inputRect.bottom,
      left: inputRect.left,
      width: inputRect.width,
      zIndex: 5000,
    },
  };
};

const planRowToDisplay = (
  row: UsBvShopifyPlanRow,
): BvResolvedUsPlanDisplay | undefined => {
  const title = row.title?.trim();
  const description = row.description?.trim();
  const imageSrc = row.image_url?.trim();
  if (!title || !description || !imageSrc) return undefined;
  return {
    title,
    description,
    imageSrc,
    markers:
      typeof row.markers === 'number' && Number.isFinite(row.markers)
        ? row.markers
        : undefined,
  };
};

const findUsShopifyPlanForCard = (
  plansResponse: UsBvShopifyPlansResponse,
  card: BvUpsellCard,
): UsBvShopifyPlanRow | undefined => {
  const plans = plansResponse?.data?.plans;
  if (!Array.isArray(plans) || plans.length === 0) return undefined;
  const target = normalizeShopifyVariantId(card.shopifyVariantId);
  const byPrimary = plans.find(
    (row) =>
      row?.variant_id &&
      normalizeShopifyVariantId(String(row.variant_id)) === target,
  );
  if (byPrimary) return byPrimary;
  if (!card.usPlanKey) return undefined;
  const altTargets = new Set(
    getAllUsBvVariantGidsForPlanKey(card.usPlanKey).map((gid) =>
      normalizeShopifyVariantId(gid),
    ),
  );
  return plans.find(
    (row) =>
      row?.variant_id &&
      altTargets.has(normalizeShopifyVariantId(String(row.variant_id))),
  );
};

export const isUsBvShopifyPlansPayloadOk = (
  plansResponse: UsBvShopifyPlansResponse,
): boolean => {
  const plans = plansResponse?.data?.plans;
  return (
    plansResponse?.status === 'ok' &&
    Array.isArray(plans) &&
    plans.length > 0
  );
};

export const checkUsBvZipServiceable = async (
  normalizedZip: string,
): Promise<boolean> => {
  const serviceabilityUrl = new URL(
    `${API_BASE_URL}/api/web_v1/blood_vision/serviceability_status`,
  );
  serviceabilityUrl.searchParams.append('zip_code', normalizedZip);
  const serviceabilityResponse = await fetch(serviceabilityUrl.toString(), {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  const serviceabilityData =
    (await serviceabilityResponse.json()) as UsBvServiceabilityResponse;
  return (
    serviceabilityData?.status === 'ok' &&
    serviceabilityData?.data?.is_serviceable === true
  );
};

export const fetchUsBvShopifyPlansForZip = async (
  normalizedZip: string,
): Promise<UsBvShopifyPlansResponse> => {
  const plansUrl = new URL(
    `${API_BASE_URL}/api/web_v1/blood_vision/shopify_plans`,
  );
  plansUrl.searchParams.append('zip_code', normalizedZip);
  const plansResponse = await fetch(plansUrl.toString(), {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  return (await plansResponse.json()) as UsBvShopifyPlansResponse;
};

const AE_SUPPORTED_CITIES_CACHE_TTL_MS = 60_000;
const AE_SUPPORTED_CITIES_API_PATH = '/api/blood-vision/ae/supported_cities/';

type AeSupportedCitiesCacheEntry = {
  cities: AeSupportedCity[];
  expiresAt: number;
};

let aeSupportedCitiesCache: AeSupportedCitiesCacheEntry | null = null;
let aeSupportedCitiesInFlight: Promise<AeSupportedCity[]> | null = null;

const parseAeSupportedCitiesResponse = (
  data: AeSupportedCitiesResponse,
): AeSupportedCity[] => {
  if (data?.status !== 'ok' || !Array.isArray(data?.data?.cities)) {
    return [];
  }
  return data.data.cities.filter(
    (city): city is AeSupportedCity =>
      typeof city?.name === 'string' && city.name.trim().length > 0,
  );
};

const requestAeSupportedCities = async (): Promise<AeSupportedCity[]> => {
  const response = await fetch(AE_SUPPORTED_CITIES_API_PATH, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  const data = (await response.json()) as AeSupportedCitiesResponse;
  return parseAeSupportedCitiesResponse(data);
};

/** Cached ~60s; dedupes concurrent callers (e.g. debounced city validation while typing). */
export const fetchAeSupportedCities = async (): Promise<AeSupportedCity[]> => {
  const now = Date.now();
  if (aeSupportedCitiesCache && aeSupportedCitiesCache.expiresAt > now) {
    return aeSupportedCitiesCache.cities;
  }

  if (!aeSupportedCitiesInFlight) {
    aeSupportedCitiesInFlight = requestAeSupportedCities()
      .then((cities) => {
        aeSupportedCitiesCache = {
          cities,
          expiresAt: Date.now() + AE_SUPPORTED_CITIES_CACHE_TTL_MS,
        };
        return cities;
      })
      .catch((error) => {
        console.error('AE supported cities fetch failed', error);
        return [];
      })
      .finally(() => {
        aeSupportedCitiesInFlight = null;
      });
  }

  return aeSupportedCitiesInFlight;
};

export const buildUsBvCardResultFromShopifyPlansResponse = (
  card: BvUpsellCard,
  plansResponse: UsBvShopifyPlansResponse,
): BvPincodeValidationResult => {
  if (!isUsBvShopifyPlansPayloadOk(plansResponse)) {
    return {
      isServiceable: false,
      reasonCode: 'not_serviceable',
      message: 'Blood Vision is not serviceable for this ZIP code yet.',
    };
  }

  const matched = findUsShopifyPlanForCard(plansResponse, card);
  const resolvedPrice = matched ? parseUsBvPlanPrice(matched) : undefined;
  if (!matched || typeof resolvedPrice !== 'number') {
    return {
      isServiceable: false,
      reasonCode: 'not_serviceable',
      message: 'This plan is not available for your ZIP code yet.',
    };
  }

  const resolvedUsPlanDisplay = planRowToDisplay(matched);
  if (!resolvedUsPlanDisplay) {
    return {
      isServiceable: false,
      reasonCode: 'not_serviceable',
      message: 'This plan is not available for your ZIP code yet.',
    };
  }

  const resolvedShopifyVariantId = matched.variant_id
    ? toShopifyVariantGid(String(matched.variant_id))
    : undefined;

  return {
    isServiceable: true,
    resolvedPrice,
    resolvedUsPlanDisplay,
    resolvedShopifyVariantId,
    message: 'ZIP code is serviceable.',
  };
};

export const getUsBvSharedCardSnapshot = (
  card: BvUpsellCard,
  committedZip: string | null,
  plansResponse: UsBvShopifyPlansResponse | null,
): BvPincodeValidationResult | null => {
  if (
    card.pincodeBehavior !== 'us_price_and_availability' ||
    !committedZip ||
    !plansResponse ||
    !isUsBvShopifyPlansPayloadOk(plansResponse)
  ) {
    return null;
  }
  return buildUsBvCardResultFromShopifyPlansResponse(card, plansResponse);
};

export interface ValidateUsZipWithMetaOptions {
  reuseShopifyPlans?: { zip: string; response: UsBvShopifyPlansResponse } | null;
}

export interface UsZipValidationWithMeta {
  result: BvPincodeValidationResult;
  shopifyPlansResponse?: UsBvShopifyPlansResponse;
  normalizedZip: string;
}

export const validateUsZipAndPricingWithMeta = async (
  card: BvUpsellCard,
  zipCode: string,
  options?: ValidateUsZipWithMetaOptions,
): Promise<UsZipValidationWithMeta> => {
  if (!isValidUsZip(zipCode)) {
    return {
      result: {
        isServiceable: false,
        reasonCode: 'invalid_pincode_format',
        message: 'Please enter a valid 5-digit ZIP code.',
      },
      normalizedZip: zipCode.trim(),
    };
  }

  const normalizedZip = zipCode.trim();

  try {
    const isZipServiceable = await checkUsBvZipServiceable(normalizedZip);
    if (!isZipServiceable) {
      return {
        result: {
          isServiceable: false,
          reasonCode: 'not_serviceable',
          message: 'Blood Vision is not serviceable for this ZIP code yet.',
        },
        normalizedZip,
      };
    }

    const reuse = options?.reuseShopifyPlans;
    let plansData: UsBvShopifyPlansResponse;
    if (
      reuse &&
      reuse.zip === normalizedZip &&
      isUsBvShopifyPlansPayloadOk(reuse.response)
    ) {
      plansData = reuse.response;
    } else {
      plansData = await fetchUsBvShopifyPlansForZip(normalizedZip);
    }

    if (!isUsBvShopifyPlansPayloadOk(plansData)) {
      return {
        result: {
          isServiceable: false,
          reasonCode: 'not_serviceable',
          message: 'Blood Vision is not serviceable for this ZIP code yet.',
        },
        normalizedZip,
      };
    }

    const result = buildUsBvCardResultFromShopifyPlansResponse(card, plansData);
    return {
      result,
      shopifyPlansResponse: plansData,
      normalizedZip,
    };
  } catch (error) {
    console.error('US BV ZIP validation failed', error);
    return {
      result: {
        isServiceable: false,
        reasonCode: 'request_failed',
        message: 'Unable to validate ZIP code right now. Please try again.',
      },
      normalizedZip,
    };
  }
};

const validateUsZipAndPricing = async (
  card: BvUpsellCard,
  zipCode: string,
  options?: ValidateUsZipWithMetaOptions,
): Promise<BvPincodeValidationResult> => {
  const { result } = await validateUsZipAndPricingWithMeta(
    card,
    zipCode,
    options,
  );
  return result;
};

const validateInPincode = async (
  pincode: string,
): Promise<BvPincodeValidationResult> => {
  try {
    const response = await fetch(
      `/api/blood-vision/india/package_list/?pincode=${pincode.trim()}`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      },
    );
    const data = (await response.json()) as InBvPackageListResponse;
    const isServiceable =
      data?.status === 'ok' && data?.data?.pincode_serviceable !== false;
    const city = data?.data?.pincode_info?.city;
    const state = data?.data?.pincode_info?.state;
    const resolvedLocationLabel =
      city && state ? `${city}, ${state}` : city || state || '';

    if (!isServiceable) {
      return {
        isServiceable: false,
        reasonCode: 'not_serviceable',
        message: 'Blood Vision is not serviceable for this pincode yet.',
      };
    }

    return {
      isServiceable: true,
      message: resolvedLocationLabel
        ? `${resolvedLocationLabel} is serviceable.`
        : 'Pincode is serviceable.',
    };
  } catch (error) {
    console.error('IN BV pincode validation failed', error);
    return {
      isServiceable: false,
      reasonCode: 'request_failed',
      message: 'Unable to validate pincode right now. Please try again.',
    };
  }
};

export const validateAeCity = async (
  cityInput: string,
): Promise<BvPincodeValidationResult> => {
  const trimmed = cityInput.trim();
  if (!trimmed) {
    return {
      isServiceable: false,
      reasonCode: 'missing_pincode',
      message: getBvStoreInputConfig(ShopifyStore.AE).missingValueMessage,
    };
  }

  try {
    const supportedCities = await fetchAeSupportedCities();
    if (!supportedCities.length) {
      return {
        isServiceable: false,
        reasonCode: 'request_failed',
        message: 'Unable to validate city right now. Please try again.',
      };
    }
    const matched = supportedCities.find((city) =>
      cityEqualsAeSupportedCity(trimmed, city),
    );
    if (!matched) {
      return {
        isServiceable: false,
        reasonCode: 'not_serviceable',
        message: 'Blood Vision is not serviceable in this city yet.',
      };
    }
    return {
      isServiceable: true,
      message: `${matched.name} is serviceable.`,
    };
  } catch (error) {
    console.error('AE BV city validation failed', error);
    return {
      isServiceable: false,
      reasonCode: 'request_failed',
      message: 'Unable to validate city right now. Please try again.',
    };
  }
};

export const getUsBvMergedDisplay = (
  store: ShopifyStore | null,
  card: BvUpsellCard,
  resolvedDisplay: BvResolvedUsPlanDisplay | undefined | null,
): {
  title: string;
  subtitle?: string;
  description: string;
  imageSrc: string;
  learnMoreAbout: string;
} => {
  const useApi =
    store === ShopifyStore.US &&
    !!resolvedDisplay &&
    card.pincodeBehavior === 'us_price_and_availability';
  if (!useApi || !resolvedDisplay) {
    return {
      title: card.title,
      subtitle: card.subtitle,
      description: card.description,
      imageSrc: card.imageSrc,
      learnMoreAbout: card.learnMoreContent.about,
    };
  }
  return {
    title: resolvedDisplay.title,
    subtitle:
      resolvedDisplay.markers != null
        ? `${resolvedDisplay.markers} markers`
        : card.subtitle,
    description: resolvedDisplay.description,
    imageSrc: card.imageSrc,
    learnMoreAbout: resolvedDisplay.description,
  };
};

export const getBvCartUpsellListingFields = (
  card: BvUpsellCard,
): { cartTitle: string; cartSubtitle: string } => {
  return {
    cartTitle: card.cartListingTitle,
    cartSubtitle: card.cartListingSubtitle,
  };
};

export type BvCartAddonDescriptionKey =
  | 'hormone_health_male'
  | 'hormone_health_female'
  | 'fasting_insulin'
  | 'diabetes_risk'
  | 'base_plan';

export const getBvCartAddonDescriptionKey = (
  cardId: string,
): BvCartAddonDescriptionKey | null => {
  if (cardId.includes('hormone-health-male')) return 'hormone_health_male';
  if (cardId.includes('hormone-health-female')) return 'hormone_health_female';
  if (cardId.includes('fasting-insulin')) return 'fasting_insulin';
  if (cardId.includes('diabetes-risk')) return 'diabetes_risk';
  if (cardId.includes('base-plan')) return 'base_plan';
  return null;
};

export interface ValidateBvPincodeOptions {
  reuseShopifyPlans?: { zip: string; response: UsBvShopifyPlansResponse } | null;
}

export const validateBvPincodeByStore = async (
  store: ShopifyStore,
  card: BvUpsellCard,
  pincode: string,
  options?: ValidateBvPincodeOptions,
): Promise<BvPincodeValidationResult> => {
  if (!pincode?.trim()) {
    return {
      isServiceable: false,
      reasonCode: 'missing_pincode',
      message: getBvStoreInputConfig(store).missingValueMessage,
    };
  }

  if (card.pincodeBehavior === 'us_price_and_availability') {
    return validateUsZipAndPricing(card, pincode, {
      reuseShopifyPlans: options?.reuseShopifyPlans,
    });
  }

  const trimmed = pincode.trim();
  if (store === ShopifyStore.AE) {
    const normalizedCity = normalizeAeCityForMatch(trimmed);
    if (normalizedCity.length < 2) {
      return {
        isServiceable: false,
        reasonCode: 'invalid_pincode_format',
        message: getBvStoreInputConfig(store).invalidFormatMessage,
      };
    }
    return validateAeCity(trimmed);
  }

  if (!isValidInPincode(trimmed)) {
    return {
      isServiceable: false,
      reasonCode: 'invalid_pincode_format',
      message: getBvStoreInputConfig(store).invalidFormatMessage,
    };
  }

  return validateInPincode(trimmed);
};
